import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lacUserService } from '../../../services/LacUserService';
import type {
  LacCreateUserInput,
  LacResetUserPasswordInput,
  LacSetUserEnabledInput,
  LacUpdateUserInput,
} from '../../../types/lac';
import { lacQueryKeys } from '../lacQueryKeys';

export type SaveLacUserInput = LacUpdateUserInput;

function invalidateLacUserPhotoQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantName: string,
  userRef: string,
) {
  void queryClient.invalidateQueries({
    queryKey: lacQueryKeys.usersByTenant(),
  });
  void queryClient.invalidateQueries({
    queryKey: lacQueryKeys.user(tenantName, userRef),
  });
}

function invalidateLacTenantOverviews(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantName?: string,
) {
  void queryClient.invalidateQueries({
    queryKey: [...lacQueryKeys.all, 'tenantOverviews'],
  });
  if (tenantName) {
    void queryClient.invalidateQueries({
      queryKey: [...lacQueryKeys.all, 'tenantOverview', tenantName],
    });
  }
}

export function useCreateLacUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LacCreateUserInput) => lacUserService.createUser(input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      invalidateLacTenantOverviews(queryClient, input.tenantName);
      if (input.createTenant) {
        void queryClient.invalidateQueries({
          queryKey: lacQueryKeys.tenants(),
        });
      }
    },
  });
}

export function useUpdateLacUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveLacUserInput) => lacUserService.updateUser(input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.user(input.tenantName, input.userRef),
      });
    },
  });
}

export function useUploadLacUserPhotoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantName,
      userRef,
      file,
    }: {
      tenantName: string;
      userRef: string;
      file: File;
    }) => lacUserService.uploadUserPhoto(tenantName, userRef, file),
    onSuccess: (_result, input) => {
      invalidateLacUserPhotoQueries(
        queryClient,
        input.tenantName,
        input.userRef,
      );
    },
  });
}

export function useDeleteLacUserPhotoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantName,
      userRef,
    }: {
      tenantName: string;
      userRef: string;
    }) => lacUserService.deleteUserPhoto(tenantName, userRef),
    onSuccess: (_result, input) => {
      invalidateLacUserPhotoQueries(
        queryClient,
        input.tenantName,
        input.userRef,
      );
    },
  });
}

export function useDeleteLacUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantName,
      userRef,
    }: {
      tenantName: string;
      userRef: string;
    }) => lacUserService.deleteUser(tenantName, userRef),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      void queryClient.removeQueries({
        queryKey: lacQueryKeys.user(variables.tenantName, variables.userRef),
      });
      void queryClient.removeQueries({
        queryKey: lacQueryKeys.userRoles(
          variables.tenantName,
          variables.userRef,
        ),
      });
      invalidateLacTenantOverviews(queryClient, variables.tenantName);
    },
  });
}

export function useSetLacUserEnabledMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LacSetUserEnabledInput) =>
      lacUserService.setUserEnabled(input),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.user(variables.tenantName, variables.userRef),
      });
    },
  });
}

export function useSetLacUserRolesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantName,
      userRef,
      roleNames,
    }: {
      tenantName: string;
      userRef: string;
      roleNames: string[];
    }) => lacUserService.setUserRoles(tenantName, userRef, roleNames),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.user(variables.tenantName, variables.userRef),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.userRoles(
          variables.tenantName,
          variables.userRef,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
    },
  });
}

export function useResetLacUserPasswordMutation() {
  return useMutation({
    mutationFn: (input: LacResetUserPasswordInput) =>
      lacUserService.resetUserPassword(input),
  });
}
